import {
  CapitalMovementGetByIdDocument,
  CapitalMovementFileTypesDocument,
  CapitalMovementListDocument,
  CapitalMovementCreateDocument,
  CapitalMovementDeleteDocument,
  CapitalMovementExportFileDocument,
  CapitalMovementUpdateDocument,
  GetCapitalMovementsDocument,
  CapitalMovementTransferSourceInKindCurrencyDocument,
} from "api/generated";
import { RequestTypes } from "api/services/enums";
import { getRequestInstance } from "api/services/utils";
import {
  DistributionReturnTypeEnumType,
  Nullable,
  Optional,
  PartialNullable,
  mapInvestorName,
} from "config";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";

import {
  ICapitalMovementExportArgs,
  ICapitalMovementModel,
  ICapitalMovementByIdReturnType,
  ICreateCapitalMovementArgs,
  IGetCapitalMovementListArgs,
  IGetCapitalMovementListReturnType,
  IUpdateCapitalMovementArgs,
  IGetCapitalMovementVars,
  IGetCapitalMovementResponse,
  IInKindCurrencyTypeModel,
} from "./types";

export async function createCapitalMovement(
  variables: ICreateCapitalMovementArgs
): Promise<Optional<Pick<ICapitalMovementModel, "capitalMovementId">>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: CapitalMovementCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    // Evict all capital movement lists so they refetch with the new item
    executeEviction(client, {
      typename: "CapitalMovement",
      fieldNames: [
        "capitalMovementPaginated",
        "capitalMovementList",
        "capitalMovementListByInvestorProfile",
        "capitalFormationCommitmentTrackingPaginated",
        "capitalFormationCommitmentTrackingInvestorsPaginated",
      ],
    });

    return res?.data?.capitalMovementCreate;
  } catch (error) {
    throw error;
  }
}

export async function getCapitalMovementList(
  variables?: IGetCapitalMovementListArgs
): Promise<IGetCapitalMovementListReturnType> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: CapitalMovementListDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return (
      res?.data?.capitalMovementPaginated && {
        ...res.data.capitalMovementPaginated,
        edges: res.data.capitalMovementPaginated.edges.map((item) => ({
          ...item,
          node: {
            ...item.node,
            investorProfile: item.node.investorProfile && {
              ...item.node.investorProfile,
              ...mapInvestorName(item.node.investorProfile),
            },
          },
        })),
      }
    );
  } catch (error) {
    throw error;
  }
}

export async function getCapitalMovementById(
  capitalMovementId: number
): Promise<Nullable<PartialNullable<ICapitalMovementByIdReturnType>>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: CapitalMovementGetByIdDocument,
      variables: {
        capitalMovementId,
      },
      fetchPolicy: "network-only",
    });

    if (!res?.data?.capitalMovementGetById) {
      return null;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { __typename, ...restData } = res.data.capitalMovementGetById;

    return {
      ...restData,
      investorProfile: {
        ...restData.investorProfile,
        ...mapInvestorName(restData.investorProfile),
      },
      returnType:
        restData.returnType as Nullable<DistributionReturnTypeEnumType>,
    };
  } catch (error) {
    throw error;
  }
}

export async function updateCapitalMovement(
  variables: IUpdateCapitalMovementArgs
): Promise<Optional<Pick<ICapitalMovementModel, "capitalMovementId">>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: CapitalMovementUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    // Evict the updated entity and all lists containing it
    executeEviction(client, {
      typename: "CapitalMovement",
      id: variables.capitalMovementId?.toString(),
      fieldNames: [
        "capitalMovementPaginated",
        "capitalMovementList",
        "capitalMovementGetById",
        "capitalMovementListByInvestorProfile",
        "capitalFormationCommitmentTrackingPaginated",
        "capitalFormationCommitmentTrackingInvestorsPaginated",
      ],
    });

    return res?.data?.capitalMovementUpdate;
  } catch (error) {
    throw error;
  }
}

export async function getCapitalMovementFileTypes(): Promise<string[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: CapitalMovementFileTypesDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.capitalMovementExportFileType ?? [];
  } catch (error) {
    throw error;
  }
}

export async function exportCapitalMovementFile(
  variables: ICapitalMovementExportArgs
): Promise<string> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: CapitalMovementExportFileDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.capitalMovementExport;
  } catch (error) {
    throw error;
  }
}

export async function deleteCapitalMovement(
  capitalMovementId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: CapitalMovementDeleteDocument,
      variables: {
        capitalMovementId,
      },
      fetchPolicy: "no-cache",
    });

    // Evict the deleted entity and all related data
    executeEviction(client, {
      typename: "CapitalMovement",
      id: capitalMovementId.toString(),
      fieldNames: [
        "capitalMovementPaginated",
        "capitalMovementList",
        "capitalMovementGetById",
        "capitalMovementListByInvestorProfile",
        "capitalFormationCommitmentTrackingPaginated",
        "capitalFormationCommitmentTrackingInvestorsPaginated",
      ],
    });

    return Boolean(res?.data?.capitalMovementDelete);
  } catch (error) {
    throw error;
  }
}

export async function getCapitalMovements(
  variables: IGetCapitalMovementVars
): Promise<IGetCapitalMovementResponse[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: GetCapitalMovementsDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.capitalMovementList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getCapitalMovementInKindCurrencies(): Promise<
  IInKindCurrencyTypeModel[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: CapitalMovementTransferSourceInKindCurrencyDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.capitalMovementTransferSourceInKindCurrency ?? [];
  } catch (error) {
    throw error;
  }
}
